// DTOs/schemas do módulo "meta" (número oficial / Meta Cloud API). Pertence ao pacote meta.
import { z } from "zod";

import type { CategoriaMeta } from "./enums";

const soDigitos = (rotulo: string) =>
  z
    .string({ required_error: `${rotulo} é obrigatório.` })
    .trim()
    .regex(/^\d{5,25}$/, `${rotulo} deve conter apenas números.`);

/** `POST /numeros/oficial` — credenciais do cliente (a conta Meta é DELE). */
export const zCriarNumeroOficial = z.object({
  apelido: z.string({ required_error: "Informe um apelido." }).trim().min(1, "Informe um apelido.").max(80),
  wabaId: soDigitos("O WABA ID"),
  phoneNumberId: soDigitos("O Phone Number ID"),
  token: z
    .string({ required_error: "Informe o token." })
    .trim()
    .min(20, "Token inválido.")
    .max(1000, "Token inválido."),
  appSecret: z
    .string({ required_error: "Informe o App Secret." })
    .trim()
    .min(8, "App Secret inválido.")
    .max(200, "App Secret inválido."),
});
export type CriarNumeroOficial = z.infer<typeof zCriarNumeroOficial>;

/** `PUT /numeros/:id/oficial/credenciais` — troca token e/ou App Secret sem recriar o número. */
export const zAtualizarCredenciaisOficial = z
  .object({
    token: z.string().trim().min(20, "Token inválido.").max(1000, "Token inválido.").optional(),
    appSecret: z.string().trim().min(8, "App Secret inválido.").max(200, "App Secret inválido.").optional(),
  })
  .refine((v) => v.token !== undefined || v.appSecret !== undefined, {
    message: "Informe o novo token e/ou o novo App Secret.",
    path: ["token"],
  });
export type AtualizarCredenciaisOficial = z.infer<typeof zAtualizarCredenciaisOficial>;

/**
 * Quantidade de variáveis posicionais ({{1}}, {{2}}…) de um componente do template.
 * `formato` MIDIA = cabeçalho de imagem/vídeo/documento (a "variável" é a URL da mídia).
 */
export interface TemplateVariavel {
  componente: "HEADER" | "BODY" | "BUTTON";
  quantidade: number;
  formato: "TEXTO" | "MIDIA";
  /** Só MIDIA: tipo do cabeçalho (a variável é a URL HTTPS da mídia). */
  midia?: "IMAGE" | "VIDEO" | "DOCUMENT";
  /** Só BUTTON: posição do botão (botão de URL com variável). */
  indiceBotao?: number;
}

export type StatusTemplateMeta =
  | "APPROVED"
  | "PENDING"
  | "REJECTED"
  | "PAUSED"
  | "DISABLED"
  | "INDISPONIVEL";

/** Template sincronizado da Meta. Sem bloqueio de uso por status: a Meta decide. */
export interface TemplateResumo {
  /** `Template.publicId`: o id que campanhas e a API usam para escolher o template. */
  publicId: string;
  /** Id do template na Meta (informativo; pode ser nulo). */
  id: string | null;
  nome: string;
  idioma: string;
  categoria: CategoriaMeta;
  status: StatusTemplateMeta;
  /** Motivo da Meta (rejeição, pausa…) traduzido. */
  motivo: string | null;
  variaveis: TemplateVariavel[];
  totalVariaveis: number;
  componentes: unknown[];
  sincronizadoEm: string;
}

export interface SincronizacaoTemplatesResultado {
  total: number;
  indisponiveis: number;
  sincronizadoEm: string;
  templates: TemplateResumo[];
}
